import { useMutation, useQueryClient } from '@tanstack/react-query'

import { createApiCall } from 'services'
import type { CertificateAdmin, CertificateGroupDivisibility } from 'types'

type CreateGroupFromExistingParams = {
  /** Existing, standalone certificates to combine into a group. Must be >= 2. */
  certificateIds: string[]
  divisibility: CertificateGroupDivisibility
  /**
   * Product for the newly-created tagless parent. Members keep their own products.
   *
   * OPTIONAL, matching the API's `parentProductId?: UUID`: a parent product is REQUIRED
   * for an `indivisible` group (there the parent IS the sellable unit, and billing and
   * inventory hang off its product) and optional for a `divisible` one (there the parent
   * is only a container, so demanding a product is friction — and a divisible group's
   * members often span different products anyway).
   */
  parentProductId?: string
}

type CreateGroupFromExistingResult = {
  parent: CertificateAdmin
  children: CertificateAdmin[]
}

type UseCreateGroupFromExistingOptions = {
  onSuccess?: (data: CreateGroupFromExistingResult) => void
  onError?: (error?: unknown) => void
}

// VRG-337 — "Group existing certificates": the retroactive counterpart to
// useCertificateGroupCreate-style flows in the create wizard (VRG-233), for
// combining ALREADY-EXISTING, standalone certificates into a group via
// POST /certificates/admin/groups/from-existing.
export const useCreateGroupFromExisting = (options?: UseCreateGroupFromExistingOptions) => {
  const queryClient = useQueryClient()

  const createGroupMutation = useMutation({
    mutationFn: async (params: CreateGroupFromExistingParams) => {
      const response = await createApiCall({
        method: 'POST',
        url: '/certificates/admin/groups/from-existing',
        data: params,
      })
      return response.data as CreateGroupFromExistingResult
    },
    onSuccess: (data) => {
      // The new parent and every linked member all change status — invalidate
      // the whole certificates list/detail tree rather than enumerating ids.
      queryClient.invalidateQueries({ queryKey: ['certificates'] })
      options?.onSuccess?.(data)
    },
    onError: (error) => {
      options?.onError?.(error)
    },
  })

  return {
    createGroupFromExisting: (params: CreateGroupFromExistingParams) => createGroupMutation.mutateAsync(params),
    isCreatingGroup: createGroupMutation.isPending,
  }
}
